import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import { OutlinedInput } from "../../components";
import { useUserContext } from "../../hooks/use-auth-context";
import { useMultiSiteContext } from "../../hooks/use-site-context";
import { useIdentityLookup, SiteInfo } from "../../hooks/use-identity-lookup";
import { useNavigate } from "react-router";
import { Views } from "../../utils/types";
import logoImage from "../../assets/SM-02.png";
import { useEffect, useState, useCallback } from "react";
import { useGoogleAuthUrl, useMicrosoftAuthUrl } from "../../hooks/use-google-auth-url";
import { ProviderConfig } from "../../hooks/use-identity-lookup";
import { colors, shadows, whiteTint } from "../../utils/colors";

// Identifier step
const IdentifierStep = ({
    onContinue,
    loading,
    error,
    initialValue = "",
}: {
    onContinue: (identifier: string) => void;
    loading: boolean;
    error: string | null;
    initialValue?: string;
}) => {
    const [identifier, setIdentifier] = useState(initialValue);
    const [touched, setTouched] = useState(false);

    const validationError = touched && !identifier.trim() ? "Username or email is required" : null;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (identifier.trim()) {
            onContinue(identifier.trim());
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
                <OutlinedInput
                    label="Username or Email"
                    value={identifier}
                    onChange={(value) => setIdentifier(value)}
                    error={validationError ?? error ?? undefined}
                    helperText={validationError ?? error ?? undefined}
                />
                <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    size="large"
                    disabled={loading}
                    endIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                    sx={{ py: 1.1, fontWeight: 700 }}
                >
                    {loading ? "Looking up..." : "Continue"}
                </Button>
            </Stack>
        </Box>
    );
};

// Auth step
const AuthStep = ({
    authMethods,
    providerConfig,
    onSignIn,
    onBack,
    loading,
}: {
    authMethods: SiteInfo["auth_methods"];
    providerConfig?: Record<string, ProviderConfig>;
    onSignIn: (password: string) => void;
    onBack: () => void;
    loading: boolean;
}) => {
    const navigate = useNavigate();
    const { getAuthorizationUrl: getGoogleUrl, loading: googleSsoLoading } = useGoogleAuthUrl();
    const { getAuthorizationUrl: getMicrosoftUrl, loading: microsoftSsoLoading } = useMicrosoftAuthUrl();
    const [ssoError, setSsoError] = useState<string | null>(null);
    const [password, setPassword] = useState("");
    const [touched, setTouched] = useState(false);

    const validationError = touched && !password ? "Password is required" : null;
    const canUsePassword = authMethods.password;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setTouched(true);
        if (password) {
            onSignIn(password);
        }
    };

    const handleGoogleSignIn = useCallback(async () => {
        setSsoError(null);
        const url = await getGoogleUrl();
        if (url) {
            const authUrl = new URL(url);
            const pluginState = authUrl.searchParams.get("state") ?? "";
            const school = window.location.hostname.split(".")[0];
            authUrl.searchParams.set("state", `${pluginState}|${school}`);
            window.location.href = authUrl.toString();
        } else {
            setSsoError("Could not reach Google. Please try again.");
        }
    }, [getGoogleUrl]);

    const handleMicrosoftSignIn = useCallback(async () => {
        setSsoError(null);
        const url = await getMicrosoftUrl();
        if (url) {
            const authUrl = new URL(url);
            const pluginState = authUrl.searchParams.get("state") ?? "";
            const school = window.location.hostname.split(".")[0];
            authUrl.searchParams.set("state", `${pluginState}|${school}`);
            window.location.href = authUrl.toString();
        } else {
            setSsoError("Could not reach Microsoft. Please try again.");
        }
    }, [getMicrosoftUrl]);

    return (
        <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
                {(authMethods.google || authMethods.microsoft) && (
                    <>
                        <Stack spacing={1.5}>
                            {authMethods.google && (
                                <>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        startIcon={
                                            googleSsoLoading
                                                ? <CircularProgress size={16} />
                                                : <GoogleIcon />
                                        }
                                        onClick={handleGoogleSignIn}
                                        disabled={googleSsoLoading}
                                        sx={{ py: 1.05, fontWeight: 600 }}
                                    >
                                        {providerConfig?.google?.label ?? "Continue with Google"}
                                    </Button>
                                    {ssoError && (
                                        <Typography variant="caption" color="error">
                                            {ssoError}
                                        </Typography>
                                    )}
                                </>
                            )}
                            {authMethods.microsoft && (
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    startIcon={
                                        microsoftSsoLoading
                                            ? <CircularProgress size={16} />
                                            : <MicrosoftIcon />
                                    }
                                    onClick={handleMicrosoftSignIn}
                                    disabled={microsoftSsoLoading}
                                    sx={{ py: 1.05, fontWeight: 600 }}
                                >
                                    {providerConfig?.microsoft?.label ?? "Continue with Microsoft"}
                                </Button>
                            )}
                        </Stack>
                        {canUsePassword && (
                            <Divider>
                                <Typography variant="caption" color="text.secondary">
                                    or use password
                                </Typography>
                            </Divider>
                        )}
                    </>
                )}

                {canUsePassword && (
                    <>
                        <OutlinedInput
                            label="Password"
                            type="password"
                            value={password}
                            onChange={(value) => setPassword(value)}
                            error={validationError ?? undefined}
                            helperText={validationError ?? undefined}
                        />

                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={loading}
                            size="large"
                            endIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                            sx={{ py: 1.1, fontWeight: 700 }}
                        >
                            {loading ? "Signing in..." : "Sign In"}
                        </Button>
                    </>
                )}
            </Stack>

            <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.5 }}>
                <Button variant="text" size="small" onClick={onBack}>
                    Back
                </Button>
                <Button
                    variant="text"
                    size="small"
                    onClick={() => navigate(Views.RESET_PASSWORD)}
                >
                    Forgot Password?
                </Button>
            </Stack>
        </Box>
    );
};

export const SignInView = () => {
    const { login, loading: authLoading, checkUserAuthentication } = useUserContext();
    const { lookup, loading: lookupLoading, error: lookupError, reset } = useIdentityLookup();
    const { blogId } = useMultiSiteContext();
    const navigate = useNavigate();

    const [step, setStep] = useState<"identifier" | "auth">("identifier");
    const [identifier, setIdentifier] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("identifier") ?? "";
    });
    const [authMethods, setAuthMethods] = useState<SiteInfo["auth_methods"]>({
        password: true,
        google: false,
        microsoft: false,
    });
    const [providerConfig, setProviderConfig] = useState<Record<string, ProviderConfig> | undefined>(undefined);
    const [lookupErrorOverride, setLookupErrorOverride] = useState<string | null>(null);

    const handleContinue = useCallback(
        async (id: string) => {
            setLookupErrorOverride(null);
            const result = await lookup(id);
            if (!result) return;

            if (!result.found) {
                setLookupErrorOverride("No account found with that username or email.");
                return;
            }

            if (result.multiple) {
                const currentSlug = window.location.hostname.split(".")[0].trim().toLowerCase();
                // Prefer the numeric blog_id (authoritative, from the already-validated
                // subdomain) since the identify endpoint's `subdomain` string can drift
                // out of sync (casing/whitespace/legacy slug) from the subsites list.
                const matched = result.sites.find(
                    (s) => (blogId != null && s.blog_id === blogId) || s.subdomain?.trim().toLowerCase() === currentSlug
                );
                if (matched) {
                    setIdentifier(id);
                    setAuthMethods(matched.auth_methods);
                    setProviderConfig(matched.provider_config);
                    setStep("auth");
                } else {
                    if (process.env.NODE_ENV !== "production") {
                        console.warn(
                            "[sign-in] identify returned no site matching the current subdomain.",
                            { currentSlug, blogId, sites: result.sites }
                        );
                    }
                    setLookupErrorOverride("Unable to determine your school. Please sign in via your school's URL.");
                }
                return;
            }

            setIdentifier(id);
            setAuthMethods(result.site.auth_methods);
            setProviderConfig(result.site.provider_config);
            setStep("auth");
        },
        [lookup, blogId]
    );

    useEffect(() => {
        const checkAuthentication = async () => {
            const isAuthenticated = await checkUserAuthentication();
            if (isAuthenticated) {
                navigate(Views.ASSET_TABLE);
            }
        };
        checkAuthentication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const param = params.get("identifier");
        if (param) {
            handleContinue(param);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSignIn = useCallback(
        async (password: string) => {
            const result = await login(identifier, password);
            if (result) {
                navigate(Views.ASSET_TABLE);
            }
        },
        [login, identifier, navigate]
    );

    const handleBack = useCallback(() => {
        setStep("identifier");
        setAuthMethods({ password: true, google: false, microsoft: false });
        setProviderConfig(undefined);
        reset();
        setLookupErrorOverride(null);
    }, [reset]);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                width: "100%",
                position: "relative",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(160deg, ${colors.navy} 0%, ${colors.navyDeep} 100%)`,
                px: 2,
                py: 4,
            }}
        >
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    top: -120,
                    right: -100,
                    width: 340,
                    height: 340,
                    borderRadius: "50%",
                    border: `48px solid ${colors.lime}`,
                    opacity: 0.08,
                    pointerEvents: "none",
                }}
            />
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    bottom: -160,
                    left: -120,
                    width: 320,
                    height: 320,
                    borderRadius: "50%",
                    border: `42px solid ${colors.sky}`,
                    opacity: 0.08,
                    pointerEvents: "none",
                }}
            />

            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    width: "100%",
                    maxWidth: 420,
                }}
            >
                <img
                    src={logoImage}
                    alt="Logo"
                    style={{
                        width: "182px",
                        height: "auto",
                        margin: "0 auto",
                        display: "block",
                    }}
                />

                <Paper
                    variant="outlined"
                    sx={{
                        p: { xs: 3, sm: 4 },
                        mt: 3,
                        borderRadius: 4,
                        borderColor: whiteTint(0.08),
                        boxShadow: shadows.heroCard,
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{ color: "text.secondary", mb: 2.5, display: "block" }}
                    >
                        {step === "identifier" ? "Sign in to your account" : "Choose a sign-in method"}
                    </Typography>

                    {step === "identifier" && (
                        <IdentifierStep
                            onContinue={handleContinue}
                            loading={lookupLoading}
                            error={lookupErrorOverride ?? lookupError}
                            initialValue={identifier}
                        />
                    )}
                    {step === "auth" && (
                        <AuthStep
                            authMethods={authMethods}
                            providerConfig={providerConfig}
                            onSignIn={handleSignIn}
                            onBack={handleBack}
                            loading={authLoading ?? false}
                        />
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default SignInView;